import { useState, useEffect, useContext } from "react";
import { useQueryClient } from "@tanstack/react-query";
import {
  Box,
  Typography,
  TextField,
  Button,
  Stack,
  Divider,
  CircularProgress,
  IconButton,
  InputAdornment,
  Switch,
} from "@mui/material";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import DnsOutlinedIcon from "@mui/icons-material/DnsOutlined";
import VpnKeyOutlinedIcon from "@mui/icons-material/VpnKeyOutlined";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import { Radio, RadioGroup, FormControlLabel } from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import api from "../api";
import { AuthContext } from "../context/AuthContext";
import { useToast } from "./ToastProvider";
import { HEADING_FONT } from "../theme/fonts";
import { COLORS } from "../theme/tokens";
import ClientPicker from "./ClientPicker";

// Flat spec design tokens (light-only) — same key names the old glass theme used.
const colors = {
  text: COLORS.ink,
  textMuted: COLORS.muted,
  textFaint: COLORS.faint,
  cardBg: COLORS.surface,
  cardBorder: COLORS.hairline,
  cardBorderHover: COLORS.borderHover,
  hover: COLORS.tile,
  divider: COLORS.hairline,
};

function SectionHeader({ icon, title, subtitle }) {
  return (
    <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 0.5 }}>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: "10px",
          background: "rgba(59, 130, 246, 0.12)",
          color: COLORS.primary,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{
            fontFamily: HEADING_FONT,
            fontSize: 14,
            fontWeight: 700,
            color: colors.text,
            lineHeight: 1.2,
            mt: "2px",
          }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{ fontSize: 12, color: colors.textMuted }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

const OWNERSHIP_OPTIONS = [
  {
    value: "self",
    label: "Self (Own Server)",
    description: "Managed in-house",
    icon: <PersonOutlineOutlinedIcon />,
  },
  {
    value: "client",
    label: "Client Server",
    description: "Owned by client",
    icon: <GroupOutlinedIcon />,
  },
];

// Reusable Add/Edit Server form body (spec §5.3). Used both inside the modal
// (AddServerModal) and the thin /add-server, /servers/:id/edit page wrappers.
//
// Props:
//   serverId  — null/undefined = create (POST); an id = edit (loads + PUT).
//   onSuccess — called after a successful save (caller closes/navigates + toasts).
//   onCancel  — called when the user clicks Cancel.
function ServerForm({ serverId = null, onSuccess, onCancel }) {
  const { isAdmin } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const toast = useToast();
  const isEdit = serverId != null;

  const [serverName, setServerName] = useState("");
  const [ownership, setOwnership] = useState("self");
  const [clientId, setClientId] = useState("");
  const [publicIp, setPublicIp] = useState("");
  const [privateIp, setPrivateIp] = useState("");
  const [hostingProvider, setHostingProvider] = useState("");
  const [panelUrl, setPanelUrl] = useState("");
  const [panelUsername, setPanelUsername] = useState("");
  const [panelPassword, setPanelPassword] = useState("");
  // Deployment (SSH) — used by the Deploy button on a domain: the ZIP is sent
  // here over SFTP and unzipped into <deployBasePath>/<domain>.
  const [sshHost, setSshHost] = useState("");
  const [sshPort, setSshPort] = useState("");
  const [sshUsername, setSshUsername] = useState("");
  const [sshPassword, setSshPassword] = useState("");
  const [showSshPassword, setShowSshPassword] = useState(false);
  const [deployBasePath, setDeployBasePath] = useState("");
  const [sshConfigured, setSshConfigured] = useState(false);
  // An SSH key is the better credential: nothing to guess, and failed password
  // attempts can't get the app's IP banned.
  const [sshPrivateKey, setSshPrivateKey] = useState("");
  const [sshKeySaved, setSshKeySaved] = useState(false);
  const [showKeyHelp, setShowKeyHelp] = useState(false);
  // How deploys reach this server: SSH, or FTP with aaPanel's API doing the
  // unzip (for servers that only hand out FTP credentials).
  const [deployMethod, setDeployMethod] = useState("ssh");
  const [ftpHost, setFtpHost] = useState("");
  const [ftpPort, setFtpPort] = useState("");
  const [ftpUsername, setFtpUsername] = useState("");
  const [ftpPassword, setFtpPassword] = useState("");
  const [showFtpPassword, setShowFtpPassword] = useState(false);
  const [ftpPasswordSaved, setFtpPasswordSaved] = useState(false);
  const [ftpRoot, setFtpRoot] = useState("");
  const [panelApiUrl, setPanelApiUrl] = useState("");
  const [panelApiKey, setPanelApiKey] = useState("");
  const [panelApiKeySaved, setPanelApiKeySaved] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [expiry, setExpiry] = useState("");
  const [cost, setCost] = useState("");
  const [autoRenew, setAutoRenew] = useState(false);
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(isEdit);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);

  // Edit mode: load the server's current values once into the fields.
  useEffect(() => {
    if (!isEdit) return;
    let active = true;
    setLoading(true);
    api
      .get(`/servers/${serverId}`)
      .then((s) => {
        if (!active) return;
        setServerName(s.name || "");
        setOwnership(s.ownership || "self");
        setClientId(s.clientId || "");
        setPublicIp(s.publicIp || "");
        setPrivateIp(s.privateIp || "");
        setHostingProvider(s.hostingProvider || "");
        setPanelUrl(s.panelUrl || "");
        setPanelUsername(s.panelUsername || "");
        // panelPassword is only present in the response for admins.
        setPanelPassword(s.panelPassword || "");
        setSshHost(s.sshHost || "");
        setSshPort(s.sshPort ? String(s.sshPort) : "");
        setSshUsername(s.sshUsername || "");
        setDeployBasePath(s.deployBasePath || "");
        // The SSH password is never sent back; an empty box with this flag set
        // means "one is stored — leave it alone to keep it".
        setSshConfigured(Boolean(s.sshConfigured));
        setSshKeySaved(Boolean(s.sshKeySaved));
        setDeployMethod(s.deployMethod === "ftp" ? "ftp" : "ssh");
        setFtpHost(s.ftpHost || "");
        setFtpPort(s.ftpPort ? String(s.ftpPort) : "");
        setFtpUsername(s.ftpUsername || "");
        setFtpRoot(s.ftpRoot || "");
        setPanelApiUrl(s.panelApiUrl || "");
        setFtpPasswordSaved(Boolean(s.ftpPasswordSaved));
        setPanelApiKeySaved(Boolean(s.panelApiKeySaved));
        setExpiry(s.expiry || "");
        setCost(s.cost != null ? String(s.cost) : "");
        setAutoRenew(!!s.autoRenew);
        setNotes(s.notes || "");
      })
      .catch((err) => {
        if (active) setLoadError(err.message || "Failed to load server");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [serverId, isEdit]);

  async function handleSubmit(e) {
    if (e?.preventDefault) e.preventDefault();
    setSaving(true);

    const payload = {
      name: serverName,
      ownership,
      clientId: ownership === "client" ? clientId || null : null,
      publicIp,
      privateIp,
      hostingProvider,
      panelUrl,
      panelUsername,
      // Only admins may write the password; omit the key entirely otherwise so a
      // non-admin's save can never overwrite the stored value.
      ...(isAdmin && { panelPassword }),
      expiry: expiry || null,
      cost: cost === "" ? null : Number(cost),
      autoRenew,
      notes,
      sshHost,
      sshPort: sshPort === "" ? null : Number(sshPort),
      sshUsername,
      deployBasePath,
      // Send the SSH password only when it was actually typed — an empty box
      // on an edit means "keep the stored one", not "clear it".
      ...(isAdmin && sshPassword ? { sshPassword } : {}),
      ...(isAdmin && sshPrivateKey ? { sshPrivateKey } : {}),
      deployMethod,
      ftpHost,
      ftpPort: ftpPort === "" ? null : Number(ftpPort),
      ftpUsername,
      ftpRoot,
      panelApiUrl,
      // Same rule as the other secrets: only sent when actually typed.
      ...(isAdmin && ftpPassword ? { ftpPassword } : {}),
      ...(isAdmin && panelApiKey ? { panelApiKey } : {}),
    };

    try {
      if (isEdit) {
        await api.put(`/servers/${serverId}`, payload);
      } else {
        await api.post("/servers", payload);
      }
      queryClient.invalidateQueries({ queryKey: ["servers"] });
      onSuccess?.({ isEdit });
    } catch (err) {
      toast.error(
        err.message || (isEdit ? "Failed to update server" : "Failed to add server")
      );
      setSaving(false);
    }
  }

  const labelSx = {
    display: "block",
    fontSize: 13,
    fontWeight: 600,
    color: colors.text,
    mb: 1,
  };

  const cardSx = {
    background: "#ffffff",
    border: `1px solid ${colors.cardBorder}`,
    borderRadius: "16px",
    boxShadow: "0 1px 3px rgba(16, 24, 40, 0.05)",
    p: 3,
  };

  const fieldSx = {
    "& .MuiOutlinedInput-root": {
      background: colors.cardBg,
      color: colors.text,
      borderRadius: "10px",
      "& fieldset": { borderColor: colors.cardBorder },
      "&:hover fieldset": { borderColor: colors.cardBorderHover },
      "&.Mui-focused fieldset": { borderColor: COLORS.primary },
    },
    "& input": { color: colors.text },
    "& textarea": { color: colors.text },
    "& input::placeholder, & textarea::placeholder": {
      color: colors.textFaint,
      opacity: 1,
    },
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress sx={{ color: COLORS.primary }} />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={cardSx}>
        <Typography sx={{ color: colors.text }}>{loadError}</Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={3}>
      {/* Section 1: Server Identity */}
      <Box sx={cardSx}>
        <SectionHeader
          icon={<DnsOutlinedIcon sx={{ fontSize: 20 }} />}
          title="Server Identity"
          subtitle="Name and ownership details"
        />
        <Divider sx={{ my: 2, borderColor: colors.divider }} />
        <Stack spacing={3}>
          <Box>
            <Typography component="label" sx={labelSx}>
              Server Name
            </Typography>
            <TextField
              placeholder="e.g. web-prod-01"
              value={serverName}
              onChange={(e) => setServerName(e.target.value)}
              required
              fullWidth
              sx={fieldSx}
            />
          </Box>

          <Box>
            <Typography component="label" sx={labelSx}>
              Ownership
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
              {OWNERSHIP_OPTIONS.map((opt) => {
                const selected = ownership === opt.value;
                return (
                  <Box
                    key={opt.value}
                    component="button"
                    type="button"
                    onClick={() => setOwnership(opt.value)}
                    sx={{
                      flex: 1,
                      textAlign: "left",
                      cursor: "pointer",
                      p: 2,
                      borderRadius: "10px",
                      border: `1px solid ${
                        selected ? COLORS.primary : colors.cardBorder
                      }`,
                      background: selected
                        ? "rgba(59, 130, 246, 0.1)"
                        : colors.cardBg,
                      transition: "all 0.15s ease",
                      "&:hover": {
                        background: selected
                          ? "rgba(59, 130, 246, 0.15)"
                          : colors.hover,
                        borderColor: selected
                          ? COLORS.primary
                          : colors.cardBorderHover,
                      },
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Box
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: "10px",
                          background: selected ? COLORS.primary : colors.hover,
                          color: selected ? "#fff" : colors.textMuted,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "all 0.15s ease",
                        }}
                      >
                        {opt.icon}
                      </Box>
                      <Box>
                        <Typography
                          sx={{
                            fontSize: 14,
                            fontWeight: 600,
                            color: selected ? COLORS.primary : colors.text,
                          }}
                        >
                          {opt.label}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: colors.textMuted }}>
                          {opt.description}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>
          </Box>

          {ownership === "client" && (
            <Box>
              <Typography component="label" sx={labelSx}>
                Client
              </Typography>
              <ClientPicker
                value={clientId}
                onChange={(cid) => setClientId(cid || "")}
                colors={colors}
                fieldSx={fieldSx}
                placeholder="Link an existing client"
              />
            </Box>
          )}
        </Stack>
      </Box>

      {/* Section 2: Network & Hosting */}
      <Box sx={cardSx}>
        <SectionHeader
          icon={<DnsOutlinedIcon sx={{ fontSize: 20 }} />}
          title="Network & Hosting"
          subtitle="IP address and provider"
        />
        <Divider sx={{ my: 2, borderColor: colors.divider }} />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 3,
          }}
        >
          <Box>
            <Typography component="label" sx={labelSx}>
              Public IP
            </Typography>
            <TextField
              placeholder="e.g. 203.0.113.1"
              value={publicIp}
              onChange={(e) => setPublicIp(e.target.value)}
              fullWidth
              sx={fieldSx}
            />
          </Box>
          <Box>
            <Typography component="label" sx={labelSx}>
              Private IP
            </Typography>
            <TextField
              placeholder="e.g. 10.0.0.5"
              value={privateIp}
              onChange={(e) => setPrivateIp(e.target.value)}
              fullWidth
              sx={fieldSx}
            />
          </Box>
          <Box>
            <Typography component="label" sx={labelSx}>
              Hosting Provider
            </Typography>
            <TextField
              placeholder="e.g. AWS, DigitalOcean"
              value={hostingProvider}
              onChange={(e) => setHostingProvider(e.target.value)}
              fullWidth
              sx={fieldSx}
            />
          </Box>
        </Box>
      </Box>

      {/* Section 3: Access & Credentials */}
      <Box sx={cardSx}>
        <SectionHeader
          icon={<VpnKeyOutlinedIcon sx={{ fontSize: 20 }} />}
          title="Access & Credentials"
          subtitle="Panel login information"
        />
        <Divider sx={{ my: 2, borderColor: colors.divider }} />
        <Stack spacing={3}>
          <Box>
            <Typography component="label" sx={labelSx}>
              Panel / Login URL
            </Typography>
            <TextField
              placeholder="https://panel.example.com"
              value={panelUrl}
              onChange={(e) => setPanelUrl(e.target.value)}
              fullWidth
              sx={fieldSx}
            />
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
              gap: 3,
            }}
          >
            <Box>
              <Typography component="label" sx={labelSx}>
                Panel Username
              </Typography>
              <TextField
                placeholder="admin"
                value={panelUsername}
                onChange={(e) => setPanelUsername(e.target.value)}
                fullWidth
                sx={fieldSx}
              />
            </Box>
            <Box>
              <Typography component="label" sx={labelSx}>
                Server Expiry Date
              </Typography>
              <TextField
                type="date"
                value={expiry}
                onChange={(e) => setExpiry(e.target.value)}
                fullWidth
                sx={fieldSx}
              />
            </Box>
          </Box>

          {/* Panel password — visible to admins only. */}
          {isAdmin && (
            <Box>
              <Typography component="label" sx={labelSx}>
                Panel Password
              </Typography>
              <TextField
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={panelPassword}
                onChange={(e) => setPanelPassword(e.target.value)}
                fullWidth
                sx={fieldSx}
                autoComplete="new-password"
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowPassword((v) => !v)}
                          edge="end"
                          tabIndex={-1}
                          sx={{ color: colors.textMuted }}
                          aria-label={
                            showPassword ? "Hide password" : "Show password"
                          }
                        >
                          {showPassword ? (
                            <VisibilityOffOutlinedIcon sx={{ fontSize: 20 }} />
                          ) : (
                            <VisibilityOutlinedIcon sx={{ fontSize: 20 }} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Box>
          )}
        </Stack>
      </Box>

      {/* Renewal & Billing (spec §4.8) */}
      <Box sx={cardSx}>
        <SectionHeader
          icon={<AutorenewIcon sx={{ fontSize: 20 }} />}
          title="Renewal & Billing"
          subtitle="Drives the Billing page amounts & auto/manual"
        />
        <Divider sx={{ my: 2, borderColor: colors.divider }} />
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
            gap: 3,
            alignItems: "start",
          }}
        >
          <Box>
            <Typography component="label" sx={labelSx}>
              Renewal Cost (₹)
            </Typography>
            <TextField
              type="number"
              placeholder="e.g. 1200"
              value={cost}
              onChange={(e) => setCost(e.target.value)}
              fullWidth
              inputProps={{ min: 0, step: "0.01" }}
              sx={fieldSx}
            />
          </Box>
          <Box>
            <Typography component="label" sx={labelSx}>
              Auto-renew
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Switch
                checked={autoRenew}
                onChange={(e) => setAutoRenew(e.target.checked)}
                sx={{
                  "& .Mui-checked": { color: COLORS.primary },
                  "& .Mui-checked + .MuiSwitch-track": {
                    backgroundColor: COLORS.primary,
                  },
                }}
              />
              <Typography sx={{ fontSize: 13.5, color: colors.textMuted }}>
                {autoRenew ? "Automatic" : "Manual"}
              </Typography>
            </Stack>
          </Box>
        </Box>
      </Box>

      {/* Section 4: Deployment (SSH) — powers the Deploy button on a domain */}
      <Box sx={cardSx}>
        <SectionHeader
          icon={<RocketLaunchOutlinedIcon sx={{ fontSize: 20 }} />}
          title="Deployment (SSH)"
          subtitle="Lets a domain's ZIP be deployed to this server"
        />
        <Divider sx={{ my: 2, borderColor: colors.divider }} />

        {/* Two ways in: a shell over SSH, or FTP with aaPanel's API doing the
            unzip. Picking one decides which fields below matter. */}
        <Typography component="label" sx={labelSx}>
          How to reach this server
        </Typography>
        <RadioGroup
          row
          value={deployMethod}
          onChange={(e) => setDeployMethod(e.target.value)}
          sx={{ mb: 2 }}
        >
          <FormControlLabel value="ssh" control={<Radio size="small" />} label="SSH / SFTP" />
          <FormControlLabel value="ftp" control={<Radio size="small" />} label="FTP + aaPanel API" />
        </RadioGroup>

        <Stack spacing={3}>
          <Box
            sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr" }, gap: 3 }}
          >
            <Box>
              <Typography component="label" sx={labelSx}>
                SSH Host
              </Typography>
              <TextField
                placeholder={publicIp || "103.239.124.53"}
                value={sshHost}
                onChange={(e) => setSshHost(e.target.value)}
                fullWidth
                sx={fieldSx}
                helperText="Leave empty to use the server's public IP"
              />
            </Box>
            <Box>
              <Typography component="label" sx={labelSx}>
                SSH Port
              </Typography>
              <TextField
                placeholder="22"
                value={sshPort}
                onChange={(e) => setSshPort(e.target.value)}
                fullWidth
                sx={fieldSx}
              />
            </Box>
          </Box>

          <Box
            sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 3 }}
          >
            <Box>
              <Typography component="label" sx={labelSx}>
                SSH Username
              </Typography>
              <TextField
                placeholder="root"
                value={sshUsername}
                onChange={(e) => setSshUsername(e.target.value)}
                fullWidth
                sx={fieldSx}
              />
            </Box>
            <Box>
              <Typography component="label" sx={labelSx}>
                Web Root
              </Typography>
              <TextField
                placeholder="/www/wwwroot"
                value={deployBasePath}
                onChange={(e) => setDeployBasePath(e.target.value)}
                fullWidth
                sx={fieldSx}
                helperText="Domain name is added to this, e.g. /www/wwwroot/example.com"
              />
            </Box>
          </Box>

          {/* SSH password — admins only, stored encrypted, never sent back. */}
          {isAdmin && (
            <Box>
              <Typography component="label" sx={labelSx}>
                SSH Password
              </Typography>
              <TextField
                type={showSshPassword ? "text" : "password"}
                placeholder={sshConfigured ? "•••••••• (saved — type to change)" : "••••••••"}
                value={sshPassword}
                onChange={(e) => setSshPassword(e.target.value)}
                fullWidth
                sx={fieldSx}
                autoComplete="new-password"
                helperText="Stored encrypted. Leave blank to keep the saved one. Not needed if you use a key below."
                slotProps={{
                  input: {
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowSshPassword((v) => !v)}
                          edge="end"
                          tabIndex={-1}
                          sx={{ color: colors.textMuted }}
                          aria-label={showSshPassword ? "Hide password" : "Show password"}
                        >
                          {showSshPassword ? (
                            <VisibilityOffOutlinedIcon sx={{ fontSize: 18 }} />
                          ) : (
                            <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
                          )}
                        </IconButton>
                      </InputAdornment>
                    ),
                  },
                }}
              />
            </Box>
          )}
        </Stack>
      </Box>

      {/* FTP + aaPanel API — for servers that only hand out FTP. The ZIP goes
          up as one file and the panel's API unpacks it server-side. */}
      {isAdmin && deployMethod === "ftp" && (
        <Box sx={cardSx}>
          <SectionHeader
            icon={<RocketLaunchOutlinedIcon sx={{ fontSize: 20 }} />}
            title="FTP + aaPanel API"
            subtitle="Used instead of SSH: FTP carries the ZIP, the panel unzips it"
          />
          <Divider sx={{ my: 2, borderColor: colors.divider }} />
          <Stack spacing={3}>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "2fr 1fr" }, gap: 3 }}>
              <Box>
                <Typography component="label" sx={labelSx}>FTP Host</Typography>
                <TextField
                  placeholder={publicIp || "103.239.124.53"}
                  value={ftpHost}
                  onChange={(e) => setFtpHost(e.target.value)}
                  fullWidth
                  sx={fieldSx}
                  helperText="Leave empty to use the server's public IP"
                />
              </Box>
              <Box>
                <Typography component="label" sx={labelSx}>FTP Port</Typography>
                <TextField
                  placeholder="21"
                  value={ftpPort}
                  onChange={(e) => setFtpPort(e.target.value)}
                  fullWidth
                  sx={fieldSx}
                />
              </Box>
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 3 }}>
              <Box>
                <Typography component="label" sx={labelSx}>FTP Username</Typography>
                <TextField
                  placeholder="site_ftp"
                  value={ftpUsername}
                  onChange={(e) => setFtpUsername(e.target.value)}
                  fullWidth
                  sx={fieldSx}
                  helperText="From aaPanel → FTP"
                />
              </Box>
              <Box>
                <Typography component="label" sx={labelSx}>FTP Password</Typography>
                <TextField
                  type={showFtpPassword ? "text" : "password"}
                  placeholder={ftpPasswordSaved ? "•••••••• (saved — type to change)" : "••••••••"}
                  value={ftpPassword}
                  onChange={(e) => setFtpPassword(e.target.value)}
                  fullWidth
                  sx={fieldSx}
                  autoComplete="new-password"
                  helperText="Stored encrypted"
                  slotProps={{
                    input: {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowFtpPassword((v) => !v)}
                            edge="end"
                            tabIndex={-1}
                            sx={{ color: colors.textMuted }}
                            aria-label={showFtpPassword ? "Hide password" : "Show password"}
                          >
                            {showFtpPassword ? (
                              <VisibilityOffOutlinedIcon sx={{ fontSize: 18 }} />
                            ) : (
                              <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    },
                  }}
                />
              </Box>
            </Box>

            <Box>
              <Typography component="label" sx={labelSx}>FTP Root</Typography>
              <TextField
                placeholder="/www/wwwroot"
                value={ftpRoot}
                onChange={(e) => setFtpRoot(e.target.value)}
                fullWidth
                sx={fieldSx}
                helperText="The folder the FTP user lands in, as set in aaPanel → FTP"
              />
            </Box>

            <Box>
              <Typography component="label" sx={labelSx}>aaPanel URL</Typography>
              <TextField
                placeholder={panelUrl || "https://103.239.124.53:13475"}
                value={panelApiUrl}
                onChange={(e) => setPanelApiUrl(e.target.value)}
                fullWidth
                sx={fieldSx}
                helperText="Leave empty to use the Panel / Login URL above"
              />
            </Box>

            <Box>
              <Typography component="label" sx={labelSx}>aaPanel API Key</Typography>
              <TextField
                type="password"
                placeholder={panelApiKeySaved ? "•••••••• (saved — type to change)" : "••••••••"}
                value={panelApiKey}
                onChange={(e) => setPanelApiKey(e.target.value)}
                fullWidth
                sx={fieldSx}
                autoComplete="new-password"
                helperText="aaPanel → Settings → API. Add this machine's IP to the panel's API allow-list too."
              />
            </Box>
          </Stack>
        </Box>
      )}

      {/* SSH key — the better credential, and the way out when the password
          isn't known or repeated attempts got the IP banned. */}
      {isAdmin && deployMethod === "ssh" && (
        <Box sx={cardSx}>
          <SectionHeader
            icon={<VpnKeyOutlinedIcon sx={{ fontSize: 20 }} />}
            title="SSH Key (recommended)"
            subtitle="Use instead of a password — nothing to guess, nothing to expire"
          />
          <Divider sx={{ my: 2, borderColor: colors.divider }} />

          <Button
            onClick={() => setShowKeyHelp((v) => !v)}
            sx={{ textTransform: "none", fontWeight: 600, color: colors.primary, mb: 1, ml: -1 }}
          >
            {showKeyHelp ? "Hide" : "How do I get a key?"}
          </Button>

          {showKeyHelp && (
            <Box
              sx={{
                background: colors.tile,
                border: `1px solid ${colors.divider}`,
                borderRadius: 2,
                p: 2,
                mb: 2,
              }}
            >
              <Typography sx={{ fontSize: 12.5, color: colors.textMuted, mb: 1 }}>
                Run these in the server's terminal (aaPanel → Terminal), then paste what the
                last command prints:
              </Typography>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1.5,
                  background: "#0F172A",
                  color: "#E2E8F0",
                  borderRadius: 1.5,
                  fontSize: 12,
                  overflowX: "auto",
                  whiteSpace: "pre",
                }}
              >
{`mkdir -p ~/.ssh && chmod 700 ~/.ssh
ssh-keygen -t ed25519 -f ~/.ssh/rabs_deploy -N "" -q
cat ~/.ssh/rabs_deploy.pub >> ~/.ssh/authorized_keys
chmod 600 ~/.ssh/authorized_keys
cat ~/.ssh/rabs_deploy`}
              </Box>
              <Typography sx={{ fontSize: 12, color: colors.textMuted, mt: 1 }}>
                Copy everything from “-----BEGIN…” to “-----END…”, both lines included.
              </Typography>
            </Box>
          )}

          <Typography component="label" sx={labelSx}>
            SSH Private Key
          </Typography>
          <TextField
            value={sshPrivateKey}
            onChange={(e) => setSshPrivateKey(e.target.value)}
            fullWidth
            multiline
            minRows={4}
            placeholder={
              sshKeySaved
                ? "A key is saved — paste a new one only to replace it"
                : "-----BEGIN OPENSSH PRIVATE KEY-----\n…\n-----END OPENSSH PRIVATE KEY-----"
            }
            sx={{ ...fieldSx, "& textarea": { fontFamily: "ui-monospace, Menlo, monospace", fontSize: 12 } }}
            helperText={
              sshKeySaved
                ? "Stored encrypted. Leave blank to keep the saved key."
                : "Stored encrypted. The key must have no passphrase."
            }
          />
        </Box>
      )}

      {/* Section 5: Notes */}
      <Box sx={cardSx}>
        <SectionHeader
          icon={<NotesOutlinedIcon sx={{ fontSize: 20 }} />}
          title="Notes"
          subtitle="Anything else worth remembering"
        />
        <Divider sx={{ my: 2, borderColor: colors.divider }} />
        <TextField
          placeholder="Any additional information..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          fullWidth
          multiline
          minRows={4}
          sx={fieldSx}
        />
      </Box>

      {/* Actions */}
      <Stack
        direction="row"
        spacing={1.5}
        justifyContent="flex-end"
        sx={{ pt: 2, borderTop: `1px solid ${colors.divider}` }}
      >
        <Button
          type="button"
          onClick={onCancel}
          sx={{
            textTransform: "none",
            color: colors.textMuted,
            px: 3,
            py: 1.25,
            borderRadius: "10px",
            "&:hover": { background: colors.hover, color: colors.text },
          }}
        >
          Cancel
        </Button>
        <Button
          type="button"
          onClick={handleSubmit}
          disabled={saving}
          sx={{
            textTransform: "none",
            fontWeight: 600,
            fontSize: 14,
            px: 3.5,
            py: 1.25,
            borderRadius: "10px",
            color: "#fff",
            background: COLORS.primary,
            boxShadow: "0 4px 14px rgba(59, 130, 246, 0.35)",
            "&:hover": {
              boxShadow: "0 8px 22px rgba(59, 130, 246, 0.45)",
              transform: "translateY(-1px)",
            },
            "&.Mui-disabled": { color: "rgba(255,255,255,0.7)" },
            transition: "all 0.15s ease",
          }}
        >
          {saving ? "Saving…" : isEdit ? "Save Changes" : "Add Server"}
        </Button>
      </Stack>
    </Stack>
  );
}

export default ServerForm;
